/**
 * The AI-administration half of the KnowIt adapter: thinkers, capability policies and AI
 * actions.
 *
 * It lives beside `knowitAutomation.ts` for the same reason that one exists — a different wire
 * convention. These routes are **REST**, not the platform's `POST /api/<noun>.<verb>` RPC, and
 * they answer **bare** objects and arrays rather than the `{data, policies}` envelope. Three
 * facts make that load-bearing:
 *
 * - `GET /api/thinkers` must be a GET. **POST on the same route creates a persona.** The
 *   transport therefore exposes a real verb per call rather than one `send()` that is always
 *   POST.
 * - Five of these endpoints answer **204 No Content**. `response.json()` throws on an empty
 *   body, so the transport's `del`/`post`/`put` must tolerate one — otherwise "remove from
 *   workspace" succeeds on the server and fails in the browser.
 * - `selection-actions` uses a **`{data}` wrapper** and answers `{success:true}` (HTTP 200) to
 *   a delete. That is a third convention inside one feature, and it is unwrapped here so no
 *   scene has to know.
 *
 * Permissions: the server gates every write with `EnsureAsync(ResourceRef.Thinker, Admin)` and
 * projects no per-entity policy, so a refusal arrives as a 403 in the server's own words. This
 * adapter does not derive abilities — it never invents a permission the server did not state.
 */
import type {
  AiProviderStatus,
  AvailableThinker,
  CapabilityCatalog,
  CapabilityPolicy,
  CatalogStatus,
  CatalogThinker,
  GroupOption,
  HiveThought,
  LocalThinker,
  LocalThinkerHealth,
  LocalThinkerInvocation,
  LocalThinkerModels,
  LocalThinkerTest,
  PersonaImportResult,
  SelectionAction,
  ThinkerBlendPreview,
  ThinkerFacet,
  ThinkerHivePolicy,
  ThinkerInvocation,
  ThinkerRecord,
  ThinkerShare,
  ThinkerTryInput,
  ThinkerTryResult,
} from "~/api/aiAdminTypes";
import { DEFAULT_HIVE_POLICY } from "~/api/aiAdminTypes";
import type {
  CapabilityPolicyDraft,
  LocalThinkerBindingDraft,
  LocalThinkerDraft,
  LocalThinkerInvokeRequest,
  PersonaPatch,
  SelectionActionDraft,
  ThinkerBinding,
  ThinkerDraft,
  ThinkerFacetInput,
  ThinkerInvokeRequest,
  ThinkerShareDraft,
  ThinkerSituationInput,
  WorkspaceThinkerDraft,
} from "~/api/client";
import { NotFoundError } from "~/api/errors";
import { bool, type Json, list, num, numOrNull, obj, str, strOrNull } from "~/api/knowitAutomation";

/** What the AI-administration surface needs from the transport it is mounted on. */
export interface AiAdminTransport {
  get(path: string): Promise<unknown>;
  /** `signal` lets a long call (Try it) be stopped from the UI. */
  post(path: string, body?: Record<string, unknown>, signal?: AbortSignal): Promise<unknown>;
  put(path: string, body?: Record<string, unknown>): Promise<unknown>;
  del(path: string): Promise<unknown>;
  /** Status + body without throwing. The provider gate reads the CODE, not the message. */
  probe(path: string, method: string): Promise<{ status: number; body: unknown }>;
}

const encode = encodeURIComponent;

/** A number the server may send as a string (guids, ids). Kept as text on this side. */
function id(value: unknown): string {
  return typeof value === "string" ? value : typeof value === "number" ? String(value) : "";
}

/** Drops undefined keys so an omitted field is genuinely absent from the request body. */
function defined(source: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(source).filter(([, value]) => value !== undefined));
}

function toAvailableThinker(raw: Json): AvailableThinker {
  const source = raw.source ? obj(raw.source) : null;
  return {
    id: str(raw.id),
    name: str(raw.name) || str(raw.id),
    description: str(raw.description),
    isLocal: bool(raw.isLocal),
    isHuman: bool(raw.isHuman),
    // The server omits `isAI` on nothing it sends today, but an older row that lacks it is an
    // AI persona — reading a missing flag as `false` printed every entry as "not AI" once.
    isAi: raw.isAI === undefined ? !bool(raw.isHuman) : bool(raw.isAI),
    provider: str(raw.provider, "unknown"),
    avatarUrl: strOrNull(raw.avatarUrl),
    areaTags: Array.isArray(raw.areaTags) ? raw.areaTags.map((tag) => str(tag)) : [],
    category: strOrNull(raw.category),
    icon: strOrNull(raw.icon),
    // Absent means usable: the field arrived with view-only sharing and predates nothing else.
    usable: raw.usable === undefined ? true : bool(raw.usable),
    kind: strOrNull(raw.kind),
    model: strOrNull(raw.model),
    source: source
      ? {
          ownerUserName: strOrNull(source.ownerUserName),
          machineId: strOrNull(source.machineId),
          machineLabel: strOrNull(source.machineLabel),
          workspaceName: strOrNull(source.workspaceName),
          workspacePath: strOrNull(source.workspacePath),
          extensionVersion: strOrNull(source.extensionVersion),
          isOnline: bool(source.isOnline),
        }
      : null,
  };
}

function toCatalogThinker(raw: Json): CatalogThinker {
  const traits = obj(raw.hrTraits);
  return {
    id: str(raw.id),
    name: str(raw.name) || str(raw.id),
    description: str(raw.description),
    category: strOrNull(raw.category),
    subcategory: strOrNull(raw.subcategory),
    icon: strOrNull(raw.icon),
    department: strOrNull(raw.department),
    personalityFocus: strOrNull(raw.personalityFocus),
    personalityPrompt: strOrNull(raw.personalityPrompt),
    added: bool(raw.added),
    provider: strOrNull(raw.provider),
    model: strOrNull(raw.model),
    skillTemplate: strOrNull(raw.skillTemplate),
    isAi: raw.isAi === undefined ? true : bool(raw.isAi),
    isExtensionVisible: raw.isExtensionVisible === undefined ? true : bool(raw.isExtensionVisible),
    decisionThreshold: num(raw.decisionThreshold, 0.8),
    temperature: numOrNull(raw.temperature),
    maxOutputTokens: numOrNull(raw.maxOutputTokens),
    defaultConfidence: numOrNull(raw.defaultConfidence),
    backendKind: strOrNull(raw.backendKind),
    billingModel: strOrNull(raw.billingModel),
    billingLabel: strOrNull(raw.billingLabel),
    hrTraits: Object.fromEntries(Object.entries(traits).map(([key, value]) => [key, num(value)])),
    tools: Array.isArray(raw.tools) ? raw.tools.map((tool) => str(tool)) : [],
    presetMemory: Array.isArray(raw.presetMemory) ? raw.presetMemory.map((m) => str(m)) : [],
    validatedBy: Array.isArray(raw.validatedBy) ? raw.validatedBy.map((v) => str(v)) : [],
    validates: Array.isArray(raw.validates) ? raw.validates.map((v) => str(v)) : [],
    isValidationThinker: bool(raw.isValidationThinker),
    origin: str(raw.origin, "knowit"),
    owned: bool(raw.owned),
  };
}

function toThinkerRecord(raw: Json): ThinkerRecord {
  return {
    id: str(raw.id),
    name: str(raw.name) || str(raw.id),
    description: str(raw.description),
    isAi: raw.isAI === undefined ? true : bool(raw.isAI),
    category: strOrNull(raw.category),
    icon: strOrNull(raw.icon),
    personalityPrompt: strOrNull(raw.personalityPrompt),
    enabled: raw.enabled === undefined ? true : bool(raw.enabled),
    origin: str(raw.origin, "knowit"),
  };
}

function toLocalThinker(raw: Json): LocalThinker {
  return {
    ...(raw.capabilities ? { capabilities: obj(raw.capabilities) } : {}),
    ...(raw.installation ? { installation: obj(raw.installation) } : {}),
    id: id(raw.id),
    thinkerId: str(raw.thinkerId),
    displayName: str(raw.displayName) || str(raw.thinkerId),
    provider: str(raw.provider, "ollama"),
    kind: str(raw.kind, "standalone"),
    baseUrl: str(raw.baseUrl),
    model: str(raw.model),
    systemPrompt: strOrNull(raw.systemPrompt),
    temperature: numOrNull(raw.temperature),
    maxOutputTokens: num(raw.maxOutputTokens, 8192),
    enabled: bool(raw.enabled),
    skillTemplate: strOrNull(raw.skillTemplate),
    webMaxSourcesPerRun: numOrNull(raw.webMaxSourcesPerRun),
    webMaxCrawlDepth: numOrNull(raw.webMaxCrawlDepth),
    webAllowedEngines: strOrNull(raw.webAllowedEngines),
    createdAt: str(raw.createdAt),
    updatedAt: str(raw.updatedAt) || str(raw.createdAt),
  };
}

function toFacet(raw: Json): ThinkerFacet {
  return {
    id: str(raw.id),
    name: str(raw.name),
    intent: str(raw.intent),
    interests: Array.isArray(raw.interests)
      ? raw.interests.map((entry) => str(entry)).filter(Boolean)
      : [],
    toward: strOrNull(raw.toward),
    amplitude: num(raw.amplitude, 1),
    pursuit: str(raw.pursuit, "propose"),
    voice: strOrNull(raw.voice),
    ...(typeof raw.authority === "boolean" ? { authority: raw.authority } : {}),
    ...(raw.binding && typeof raw.binding === "object"
      ? {
          binding: {
            role: strOrNull((raw.binding as Json).role),
            capability: strOrNull((raw.binding as Json).capability),
            thinkerId: strOrNull((raw.binding as Json).thinkerId),
          },
        }
      : {}),
    ...(Array.isArray(raw.acts)
      ? { acts: raw.acts.map((entry) => str(entry)).filter(Boolean) }
      : {}),
    ...(typeof raw.sliceChars === "number" ? { sliceChars: raw.sliceChars } : {}),
    ...(typeof raw.enabled === "boolean" ? { enabled: raw.enabled } : {}),
  };
}

/** `{ facets: [...] }` is what both facets routes answer, on GET and on PUT (the cleaned list). */
function toFacets(raw: Json): ThinkerFacet[] {
  return list(raw.facets).map(toFacet);
}

/** Blank id and empty one-liners stay off the wire; the server slugs the id from the name. */
function facetBody(facet: ThinkerFacetInput): Record<string, unknown> {
  return defined({
    id: facet.id?.trim() || undefined,
    name: facet.name,
    intent: facet.intent,
    interests: facet.interests,
    toward: facet.toward?.trim() || undefined,
    amplitude: facet.amplitude,
    pursuit: facet.pursuit,
    voice: facet.voice?.trim() || undefined,
    authority: facet.authority === true ? true : undefined,
    // BRONCO Opus 03: the seat template. An empty binding stays off the wire (= the thinker itself).
    binding:
      facet.binding && (facet.binding.role || facet.binding.capability || facet.binding.thinkerId)
        ? defined({
            role: facet.binding.role?.trim() || undefined,
            capability: facet.binding.capability?.trim() || undefined,
            thinkerId: facet.binding.thinkerId?.trim() || undefined,
          })
        : undefined,
    acts: facet.acts && facet.acts.length > 0 ? facet.acts : undefined,
    sliceChars: facet.sliceChars ?? undefined,
    enabled: facet.enabled === false ? false : undefined,
  });
}

function toBlendPreview(raw: Json): ThinkerBlendPreview {
  return {
    weights: list(raw.weights).map((entry) => ({
      id: str(entry.id),
      name: str(entry.name),
      weight: num(entry.weight),
      shared: Array.isArray(entry.shared) ? entry.shared.map((term) => str(term)) : [],
    })),
    prompt: str(raw.prompt),
  };
}

/** `{ hive: {...} }` is what the hive routes answer, on GET and on PUT. Missing fields fall to the defaults. */
function toHivePolicy(raw: Json): ThinkerHivePolicy {
  const hive = raw.hive && typeof raw.hive === "object" ? (raw.hive as Json) : raw;
  return {
    enabled: hive.enabled === true,
    mode: hive.mode === "turns" ? "turns" : "parallel",
    maxDebateRounds: num(hive.maxDebateRounds, DEFAULT_HIVE_POLICY.maxDebateRounds),
    maxImpasses: num(hive.maxImpasses, DEFAULT_HIVE_POLICY.maxImpasses),
    facetWords: num(hive.facetWords, DEFAULT_HIVE_POLICY.facetWords),
  };
}

function toHiveThought(value: unknown): HiveThought | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Json;
  const impasse = (entry: Json) => ({
    id: str(entry.id),
    from: str(entry.from),
    to: str(entry.to),
    claim: str(entry.claim),
    why: str(entry.why),
    votes: list(entry.votes).map((vote) => ({
      facetId: str(vote.facetId),
      name: str(vote.name),
      weight: num(vote.weight),
      for: str(vote.for),
      why: str(vote.why),
    })),
    status:
      entry.status === "ruled" || entry.status === "resolved"
        ? (entry.status as "ruled" | "resolved")
        : ("unresolved" as const),
    resolution: strOrNull(entry.resolution),
    forFrom: num(entry.forFrom),
    forTo: num(entry.forTo),
    rounds: num(entry.rounds),
  });
  const decision = raw.decision && typeof raw.decision === "object" ? (raw.decision as Json) : null;
  return {
    views: list(raw.views).map((view) => ({
      facetId: str(view.facetId),
      name: str(view.name),
      weight: num(view.weight),
      position: str(view.position),
      view: str(view.view),
      confidence: num(view.confidence),
      objections: list(view.objections).map((objection) => ({
        to: str(objection.to),
        claim: str(objection.claim),
        why: str(objection.why),
      })),
      valid: view.valid !== false,
    })),
    impasses: list(raw.impasses).map(impasse),
    decision: decision
      ? {
          authorityId: str(decision.authorityId),
          authorityName: str(decision.authorityName),
          answer: str(decision.answer),
          tookFrom: Array.isArray(decision.tookFrom) ? decision.tookFrom.map((x) => str(x)) : [],
          dissent: Array.isArray(decision.dissent) ? decision.dissent.map((x) => str(x)) : [],
        }
      : null,
    unresolved: list(raw.unresolved).map(impasse),
    calls: num(raw.calls),
    durationMs: num(raw.durationMs),
    policy: raw.policy && typeof raw.policy === "object" ? toHivePolicy(raw.policy as Json) : null,
  };
}

/** The body of a TRY: blank optionals stay off the wire. */
function tryBody(input: ThinkerTryInput): Record<string, unknown> {
  return {
    prompt: input.prompt,
    ...(typeof input.hive === "boolean" ? { hive: input.hive } : {}),
    ...(input.rulings && input.rulings.length > 0
      ? {
          rulings: input.rulings.map((ruling) =>
            defined({
              impasseId: ruling.impasseId,
              for: ruling.for,
              note: ruling.note?.trim() || undefined,
            }),
          ),
        }
      : {}),
    ...(input.interests && input.interests.length > 0 ? { interests: input.interests } : {}),
    ...(input.toward?.trim() ? { toward: input.toward.trim() } : {}),
    ...(typeof input.maxOutputTokens === "number"
      ? { maxOutputTokens: input.maxOutputTokens }
      : {}),
  };
}

function toTryResult(raw: Json): ThinkerTryResult {
  const blend = raw.blend && typeof raw.blend === "object" ? (raw.blend as Json) : null;
  return {
    text: str(raw.text),
    reasoning: strOrNull(raw.reasoning),
    model: str(raw.model),
    durationMs: num(raw.durationMs),
    inputTokens: num(raw.inputTokens),
    outputTokens: num(raw.outputTokens),
    systemPrompt: strOrNull(raw.systemPrompt),
    hive: toHiveThought(raw.hive),
    blend: blend
      ? {
          weights: list(blend.weights).map((entry) => ({
            id: str(entry.id),
            name: str(entry.name),
            weight: num(entry.weight),
            shared: Array.isArray(entry.shared) ? entry.shared.map((term) => str(term)) : [],
            semantic: typeof entry.semantic === "number" ? entry.semantic : null,
          })),
          method: str(blend.method, "lexical"),
          prompt: str(blend.prompt),
        }
      : null,
  };
}

function toShare(raw: Json): ThinkerShare {
  return {
    id: id(raw.id),
    thinkerId: str(raw.thinkerId),
    kind: str(raw.kind, "persona"),
    recipientTeamId: strOrNull(raw.recipientTeamId),
    recipientUserId: strOrNull(raw.recipientUserId),
    recipientGroupId: strOrNull(raw.recipientGroupId),
    expiresAt: strOrNull(raw.expiresAt),
    role: str(raw.role, "use"),
  };
}

function toPolicy(raw: Json): CapabilityPolicy {
  return {
    id: id(raw.id),
    scope: str(raw.scope, "team"),
    scopeId: strOrNull(raw.scopeId),
    principalKind: str(raw.principalKind),
    principalId: str(raw.principalId),
    permissionKey: str(raw.permissionKey),
    effect: str(raw.effect, "deny"),
    createdBy: strOrNull(raw.createdBy),
    createdAt: str(raw.createdAt),
  };
}

function toSelectionAction(raw: Json): SelectionAction {
  return {
    id: id(raw.id),
    title: str(raw.title),
    icon: str(raw.icon, "zap"),
    prompt: str(raw.prompt),
    shortcut: strOrNull(raw.shortcut),
    showInMenu: raw.showInMenu === undefined ? true : bool(raw.showInMenu),
    sortOrder: num(raw.sortOrder),
    scope: str(raw.scope, "User"),
    isBuiltIn: bool(raw.isBuiltIn),
  };
}

/** The `{data}` wrapper `selection-actions` uses — not the `{data, policies}` envelope. */
function unwrap(raw: unknown): unknown {
  const payload = obj(raw);
  return "data" in payload ? payload.data : raw;
}

/**
 * The menu's order: personal actions first (they are what somebody deliberately made), then
 * the built-ins, each group by its own sort order. Mirrors the shipped kms ordering so the
 * two surfaces cannot disagree about which action is first.
 */
function orderActions(actions: SelectionAction[]): SelectionAction[] {
  const byOrder = (a: SelectionAction, b: SelectionAction) => a.sortOrder - b.sortOrder;
  return [
    ...actions.filter((action) => !action.isBuiltIn).sort(byOrder),
    ...actions.filter((action) => action.isBuiltIn).sort(byOrder),
  ];
}

export function createKnowItAiAdmin(transport: AiAdminTransport) {
  const localBody = (draft: LocalThinkerDraft): Record<string, unknown> =>
    defined({
      thinkerId: draft.thinkerId,
      displayName: draft.displayName,
      model: draft.model,
      provider: draft.provider,
      baseUrl: draft.baseUrl,
      systemPrompt: draft.systemPrompt,
      temperature: draft.temperature,
      maxOutputTokens: draft.maxOutputTokens,
      enabled: draft.enabled,
      skillTemplate: draft.skillTemplate,
      webMaxSourcesPerRun: draft.webMaxSourcesPerRun,
      webMaxCrawlDepth: draft.webMaxCrawlDepth,
      webAllowedEngines: draft.webAllowedEngines,
    });

  const thinkerBody = (draft: ThinkerDraft): Record<string, unknown> =>
    defined({
      name: draft.name,
      description: draft.description,
      // The wire spells it `isAI`; Slate's vocabulary is `isAi` everywhere else.
      isAI: draft.isAi,
      thinkerId: draft.thinkerId,
      displayName: draft.displayName,
      personalityPrompt: draft.personalityPrompt,
      personalityFocus: draft.personalityFocus,
      department: draft.department,
      category: draft.category,
      subcategory: draft.subcategory,
      icon: draft.icon,
    });

  const actionBody = (draft: SelectionActionDraft): Record<string, unknown> => ({
    title: draft.title,
    prompt: draft.prompt,
    icon: draft.icon ?? "zap",
    description: draft.description ?? null,
    shortcut: draft.shortcut ?? null,
    showInMenu: draft.showInMenu ?? true,
    sortOrder: draft.sortOrder ?? 0,
  });

  const local = {
    list: async (): Promise<LocalThinker[]> =>
      list(await transport.get("local-thinkers")).map(toLocalThinker),

    create: async (draft: LocalThinkerDraft): Promise<LocalThinker> =>
      toLocalThinker(obj(await transport.post("local-thinkers", localBody(draft)))),

    update: async (id: string, draft: LocalThinkerDraft): Promise<LocalThinker> =>
      toLocalThinker(obj(await transport.put(`local-thinkers/${encode(id)}`, localBody(draft)))),

    delete: async (id: string): Promise<void> => {
      await transport.del(`local-thinkers/${encode(id)}`);
    },

    /**
     * The `_` cache-buster is load-bearing even though the endpoint sets `no-store`: a browser
     * or intermediary that ignores it keeps serving a list captured before the user ran
     * `ollama pull`, and a missing model looks like a broken dropdown rather than a stale one.
     */
    models: async (baseUrl: string): Promise<LocalThinkerModels> => {
      const query = new URLSearchParams({ _: String(Date.now()) });
      if (baseUrl) {
        query.set("baseUrl", baseUrl);
      }
      const raw = obj(await transport.get(`local-thinkers/models?${query.toString()}`));
      return {
        reachable: bool(raw.reachable),
        error: strOrNull(raw.error),
        models: list(raw.models).map((model) => ({
          name: str(model.name),
          size: numOrNull(model.size),
          parameterSize: strOrNull(model.parameterSize),
        })),
      };
    },

    health: async (baseUrl: string, model?: string): Promise<LocalThinkerHealth> => {
      const query = new URLSearchParams();
      if (baseUrl) {
        query.set("baseUrl", baseUrl);
      }
      if (model) {
        query.set("model", model);
      }
      const suffix = query.toString();
      const raw = obj(await transport.get(`local-thinkers/health${suffix ? `?${suffix}` : ""}`));
      return {
        reachable: bool(raw.reachable),
        models: Array.isArray(raw.models) ? raw.models.map((name) => str(name)) : [],
        // Null, not false: the server only answers this when a model was named, and an
        // unasked question must not read as "the model is missing".
        modelInstalled: typeof raw.modelInstalled === "boolean" ? raw.modelInstalled : null,
        error: strOrNull(raw.error),
      };
    },

    test: async (baseUrl: string): Promise<LocalThinkerTest> => {
      const raw = obj(await transport.post("local-thinkers/test", { baseUrl }));
      return {
        reachable: bool(raw.reachable),
        modelCount: num(raw.modelCount),
        models: Array.isArray(raw.models) ? raw.models.map((name) => str(name)) : [],
        error: strOrNull(raw.error),
      };
    },

    /** 404 means "no binding", which is an answer — it is returned as null, not thrown. */
    binding: async (thinkerId: string): Promise<LocalThinker | null> => {
      try {
        return toLocalThinker(
          obj(await transport.get(`local-thinkers/binding/${encode(thinkerId)}`)),
        );
      } catch (error) {
        if (error instanceof NotFoundError) {
          return null;
        }
        throw error;
      }
    },

    setBinding: async (
      thinkerId: string,
      binding: LocalThinkerBindingDraft,
    ): Promise<LocalThinker> =>
      toLocalThinker(
        obj(
          await transport.put(
            `local-thinkers/binding/${encode(thinkerId)}`,
            defined({
              provider: binding.provider ?? "ollama",
              model: binding.model,
              displayName: binding.displayName,
              baseUrl: binding.baseUrl,
              systemPrompt: binding.systemPrompt,
              temperature: binding.temperature,
              maxOutputTokens: binding.maxOutputTokens,
              enabled: binding.enabled,
              skillTemplate: binding.skillTemplate,
            }),
          ),
        ),
      ),

    invoke: async (request: LocalThinkerInvokeRequest): Promise<LocalThinkerInvocation> => {
      const raw = obj(await transport.post("local-thinkers/invoke", defined({ ...request })));
      return {
        ok: bool(raw.ok),
        thinkerId: str(raw.thinkerId, request.thinkerId),
        model: str(raw.model),
        text: str(raw.text),
        tokensUsed: num(raw.tokensUsed),
        durationMs: num(raw.durationMs),
        // Stated, never assumed: a local thinker cannot open an artifact handle.
        hasTools: bool(raw.hasTools),
        promptCeilingExceeded: bool(raw.promptCeilingExceeded),
        promptCeilingTokens: num(raw.promptCeilingTokens),
        approxPromptTokens: num(raw.approxPromptTokens),
        error: strOrNull(raw.error),
        message: strOrNull(raw.message),
      };
    },
  };

  const thinkers = {
    /** GET, always. POSTing to this route creates a persona. */
    available: async (): Promise<AvailableThinker[]> =>
      list(await transport.get("thinkers")).map(toAvailableThinker),

    suggested: async (documentId: string): Promise<AvailableThinker[]> =>
      list(await transport.get(`thinkers/suggested/${encode(documentId)}`)).map(toAvailableThinker),

    catalog: async (): Promise<CatalogThinker[]> =>
      list(await transport.get("thinkers/catalog")).map(toCatalogThinker),

    catalogStatus: async (): Promise<CatalogStatus> => {
      const raw = obj(await transport.get("thinkers/catalog/status"));
      return {
        status: str(raw.status, "ok"),
        lastError: strOrNull(raw.lastError),
        lastRefreshAt: strOrNull(raw.lastRefreshAt),
        count: num(raw.count),
      };
    },

    persona: async (id: string): Promise<CatalogThinker> =>
      toCatalogThinker(obj(await transport.get(`thinkers/personas/${encode(id)}`))),

    /**
     * Only the keys the caller actually set go on the wire. The server treats an omitted field
     * as "keep whatever this persona currently resolves to", so sending explicit nulls would
     * blank a rented persona's record on a one-field edit.
     */
    updatePersona: async (id: string, patch: PersonaPatch): Promise<CatalogThinker> =>
      toCatalogThinker(
        obj(await transport.put(`thinkers/personas/${encode(id)}`, defined({ ...patch }))),
      ),

    personaFacets: {
      get: async (id: string): Promise<ThinkerFacet[]> =>
        toFacets(obj(await transport.get(`thinkers/personas/${encode(id)}/facets`))),

      // The whole list goes on the wire — an omitted facet IS a removal here, unlike the
      // persona patch above where an omitted field is inherited.
      put: async (id: string, facets: ThinkerFacetInput[]): Promise<ThinkerFacet[]> =>
        toFacets(
          obj(
            await transport.put(`thinkers/personas/${encode(id)}/facets`, {
              facets: facets.map(facetBody),
            }),
          ),
        ),
    },

    personaHive: {
      get: async (id: string): Promise<ThinkerHivePolicy> =>
        toHivePolicy(obj(await transport.get(`thinkers/personas/${encode(id)}/hive`))),
      put: async (id: string, hive: ThinkerHivePolicy): Promise<ThinkerHivePolicy> =>
        toHivePolicy(obj(await transport.put(`thinkers/personas/${encode(id)}/hive`, { hive }))),
    },

    previewBlend: async (input: {
      facets: ThinkerFacetInput[];
      situation: ThinkerSituationInput;
    }): Promise<ThinkerBlendPreview> =>
      toBlendPreview(
        obj(
          await transport.post("thinkers/facets/preview", {
            facets: input.facets.map(facetBody),
            situation: defined({
              intent: input.situation.intent,
              interests: input.situation.interests ?? [],
              toward: input.situation.toward?.trim() || undefined,
            }),
          }),
        ),
      ),

    // The library answers either a bare list or `{ facets: [...] }` like the other facets routes.
    facetLibrary: async (): Promise<ThinkerFacet[]> => {
      const raw = await transport.get("thinkers/facets/library");
      return Array.isArray(raw) ? raw.map((entry) => toFacet(obj(entry))) : toFacets(obj(raw));
    },

    // TRY IT: one real completion as the thinker. The server's 400 carries the message as is.
    tryLocal: async (
      rowId: string,
      input: ThinkerTryInput,
      signal?: AbortSignal,
    ): Promise<ThinkerTryResult> =>
      toTryResult(
        obj(await transport.post(`local-thinkers/${encode(rowId)}/try`, tryBody(input), signal)),
      ),
    tryPersona: async (
      personaId: string,
      input: ThinkerTryInput,
      signal?: AbortSignal,
    ): Promise<ThinkerTryResult> =>
      toTryResult(
        obj(
          await transport.post(
            `thinkers/personas/${encode(personaId)}/try`,
            tryBody(input),
            signal,
          ),
        ),
      ),

    importPersonas: async (
      options: { includeBuiltIns?: boolean } = {},
    ): Promise<PersonaImportResult> => {
      const includeBuiltIns = options.includeBuiltIns !== false;
      const raw = obj(
        await transport.post(`thinkers/personas/import?includeBuiltIns=${includeBuiltIns}`),
      );
      return {
        builtInsSeeded: num(raw.builtInsSeeded),
        imported: num(raw.imported),
        updated: num(raw.updated),
        skipped: num(raw.skipped),
        total: num(raw.total),
        error: strOrNull(raw.error),
      };
    },

    create: async (draft: ThinkerDraft): Promise<ThinkerRecord> =>
      toThinkerRecord(obj(await transport.post("thinkers", thinkerBody(draft)))),

    update: async (id: string, draft: ThinkerDraft): Promise<ThinkerRecord> =>
      toThinkerRecord(obj(await transport.put(`thinkers/${encode(id)}`, thinkerBody(draft)))),

    delete: async (id: string): Promise<void> => {
      await transport.del(`thinkers/${encode(id)}`);
    },

    addToWorkspace: async (draft: WorkspaceThinkerDraft): Promise<void> => {
      await transport.post(
        "thinkers/team",
        defined({
          thinkerId: draft.thinkerId,
          kind: draft.kind ?? "persona",
          humanUserId: draft.humanUserId,
          areaTags: draft.areaTags,
          provider: draft.provider,
          model: draft.model,
          skillTemplate: draft.skillTemplate,
        }),
      );
    },

    removeFromWorkspace: async (id: string): Promise<void> => {
      await transport.del(`thinkers/team/${encode(id)}`);
    },

    setBinding: async (id: string, binding: ThinkerBinding): Promise<void> => {
      // Nulls are meaningful here — they are how a workspace clears an override back to the
      // server default — so this body is sent whole rather than through `defined`.
      await transport.put(`thinkers/team/${encode(id)}/binding`, {
        provider: binding.provider ?? null,
        model: binding.model ?? null,
        skillTemplate: binding.skillTemplate ?? null,
      });
    },

    groups: async (): Promise<GroupOption[]> =>
      list(await transport.get("thinkers/groups")).map((group) => ({
        id: id(group.id),
        name: str(group.name),
      })),

    shares: async (id: string): Promise<ThinkerShare[]> =>
      list(await transport.get(`thinkers/${encode(id)}/shares`)).map(toShare),

    share: async (thinkerId: string, draft: ThinkerShareDraft): Promise<{ id: string }> => {
      const raw = obj(
        await transport.post(
          `thinkers/${encode(thinkerId)}/share`,
          defined({
            kind: draft.kind ?? "persona",
            recipientUserId: draft.recipientUserId,
            recipientTeamId: draft.recipientTeamId,
            recipientGroupId: draft.recipientGroupId,
            expiresAt: draft.expiresAt,
            role: draft.role ?? "use",
          }),
        ),
      );
      return { id: id(raw.id) };
    },

    revokeShare: async (grantId: string): Promise<void> => {
      await transport.del(`thinkers/shares/${encode(grantId)}`);
    },

    invoke: async (request: ThinkerInvokeRequest): Promise<ThinkerInvocation> => {
      const raw = obj(await transport.post("thinkers/invoke", defined({ ...request })));
      const usage = obj(raw.usage);
      return {
        thinkerId: str(raw.thinkerId, request.thinkerId),
        result: str(raw.result),
        conversationId: strOrNull(raw.conversationId),
        model: strOrNull(raw.model),
        inputTokens: num(usage.inputTokens),
        outputTokens: num(usage.outputTokens),
      };
    },

    local,

    localHive: {
      get: async (id: string): Promise<ThinkerHivePolicy> =>
        toHivePolicy(obj(await transport.get(`local-thinkers/${encode(id)}/hive`))),
      put: async (id: string, hive: ThinkerHivePolicy): Promise<ThinkerHivePolicy> =>
        toHivePolicy(obj(await transport.put(`local-thinkers/${encode(id)}/hive`, { hive }))),
    },

    localFacets: {
      get: async (id: string): Promise<ThinkerFacet[]> =>
        toFacets(obj(await transport.get(`local-thinkers/${encode(id)}/facets`))),

      put: async (id: string, facets: ThinkerFacetInput[]): Promise<ThinkerFacet[]> =>
        toFacets(
          obj(
            await transport.put(`local-thinkers/${encode(id)}/facets`, {
              facets: facets.map(facetBody),
            }),
          ),
        ),
    },
  };

  const capabilities = {
    policies: async (): Promise<CapabilityPolicy[]> =>
      list(await transport.get("capability-policies")).map(toPolicy),

    catalog: async (): Promise<CapabilityCatalog> => {
      const raw = obj(await transport.get("capability-policies/capabilities"));
      const strings = (value: unknown) =>
        Array.isArray(value) ? value.map((entry) => str(entry)).filter(Boolean) : [];
      return {
        skills: list(raw.skills).map((skill) => ({
          permissionKey: str(skill.permissionKey),
          id: str(skill.id),
          name: str(skill.name) || str(skill.id),
          sideEffect: str(skill.sideEffect, "none"),
        })),
        wildcards: strings(raw.wildcards),
        principalKinds: strings(raw.principalKinds),
        thinkerTemplates: strings(raw.thinkerTemplates),
        effects: strings(raw.effects),
        note: str(raw.note),
      };
    },

    create: async (draft: CapabilityPolicyDraft): Promise<CapabilityPolicy> =>
      toPolicy(
        obj(
          await transport.post(
            "capability-policies",
            defined({
              principalKind: draft.principalKind ?? "thinker_template",
              principalId: draft.principalId,
              permissionKey: draft.permissionKey,
              effect: draft.effect ?? "deny",
              scope: draft.scope,
              scopeId: draft.scopeId,
            }),
          ),
        ),
      ),

    delete: async (id: string): Promise<void> => {
      await transport.del(`capability-policies/${encode(id)}`);
    },
  };

  const aiActions = {
    /**
     * Two reads, one list. The built-ins are code on the server and the personal ones are the
     * caller's rows; the menu shows them together, so they are merged here rather than in
     * three different scenes.
     */
    list: async (): Promise<SelectionAction[]> => {
      const [system, user] = await Promise.all([
        transport.get("selection-actions/system"),
        transport.get("selection-actions/user"),
      ]);
      return orderActions([
        ...list(unwrap(system)).map(toSelectionAction),
        ...list(unwrap(user)).map(toSelectionAction),
      ]);
    },

    /**
     * W6 §B3 — the MERGED, SCOPED list the in-editor menus need.
     *
     * `list()` above fetches `system` + `user` and merges them here, which structurally cannot
     * see a team/project/document-scoped action. This route makes the server do the merge, and
     * the server dedupes on **Title** (case-insensitively) rather than id — the built-in ids
     * differ between the client fallback (`compact`) and the server (`system-compact`), so
     * nothing may key on id equality across the two.
     *
     * Scope ids are passed through as given: the caller scopes to entities the viewer can
     * already read, because the team/project/document GET routes carry explicit
     * `// TODO: Add authorization check` comments and are gated only by `[Authorize]`.
     */
    listScoped: async (
      query: { teamId?: string; projectId?: string; documentId?: string } = {},
    ): Promise<SelectionAction[]> => {
      const params = new URLSearchParams();
      if (query.teamId) params.set("teamId", query.teamId);
      if (query.projectId) params.set("projectId", query.projectId);
      if (query.documentId) params.set("documentId", query.documentId);
      const suffix = params.toString();
      const raw = await transport.get(`selection-actions${suffix ? `?${suffix}` : ""}`);
      return orderActions(list(unwrap(raw)).map(toSelectionAction));
    },

    create: async (draft: SelectionActionDraft): Promise<SelectionAction> =>
      toSelectionAction(
        obj(unwrap(await transport.post("selection-actions/user", actionBody(draft)))),
      ),

    update: async (id: string, draft: SelectionActionDraft): Promise<SelectionAction> =>
      toSelectionAction(
        obj(unwrap(await transport.put(`selection-actions/${encode(id)}`, actionBody(draft)))),
      ),

    delete: async (id: string): Promise<void> => {
      await transport.del(`selection-actions/${encode(id)}`);
    },

    reorder: async (items: Array<{ id: string; sortOrder: number }>): Promise<void> => {
      await transport.post("selection-actions/reorder", { items });
    },

    /**
     * The gate fails OPEN. Only an explicit `503 provider_not_configured` reports
     * `configured:false`; an old backend's 404, a 401, any other status and a transport
     * failure all report configured, because a false "configure your provider" banner in
     * front of a working AI is worse than no banner at all.
     */
    providerStatus: async (): Promise<AiProviderStatus> => {
      try {
        const { status, body } = await transport.probe("ai/agents/ping", "POST");
        const payload = obj(body);
        if (status >= 200 && status < 300) {
          return { configured: true, reachable: true, provider: strOrNull(payload.provider) };
        }
        if (status === 503) {
          if (str(payload.code) === "provider_not_configured") {
            return { configured: false, reachable: true, provider: null };
          }
          return { configured: true, reachable: false, provider: null };
        }
        return { configured: true, reachable: true, provider: null };
      } catch {
        return { configured: true, reachable: false, provider: null };
      }
    },
  };

  return { thinkers, capabilities, aiActions };
}
